import { useEffect, useMemo, useRef, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { z } from 'zod';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useRootData } from '@modules/root';
import { CalendarMobile } from '@rocco/ui/calendar/components/calendar-mobile';
import { TimePickerMobile } from '@rocco/ui/calendar/components/time-picker-mobile';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { BookingConfirmationMobile } from './BookingConfirmationMobile';
import { BookingResultMobile } from './BookingResultMobile';
import type { BookingProfessional, ConsultedMember } from '../types';
import { getTimezoneShort } from '../utils/calendar';

/**
 * Mobile booking flow component that manages the complete meeting scheduling process.
 *
 * Handles three phases:
 * 1. Schedule - Calendar, time picker, and duration selection
 * 2. Confirmation - User details collection and booking confirmation
 * 3. Result - Success or failure message display
 *
 * Supports both one-on-one meetings with specific members and random assignment
 * to available team members. Includes form validation, timezone handling, and
 * availability checking.
 */

dayjs.extend(utc);
dayjs.extend(timezone);

const confirmationDetailsSchema = z.object({
    userName: z
        .string()
        .trim()
        .min(1, { message: 'Please enter your name' })
        .max(50, { message: 'Name must be 50 characters or fewer' }),
    userEmail: z
        .string()
        .trim()
        .email({ message: 'Please enter a valid email address' }),
    userCompany: z
        .string()
        .trim()
        .max(50, { message: 'Company name must be 50 characters or fewer' }),
});

type ConfirmationDetails = z.infer<typeof confirmationDetailsSchema>;

type BookingPhase = 'schedule' | 'confirmation' | 'result';

interface FreeDay {
    date: string;
    freeSlots: Array<{ label: string; timestamp: number }>;
}

interface BookingScheduleControlsMobileProps {
    sessionTitle?: string;
    timezoneName: string;
    onTimezoneChange: (timezone: string) => void;
    availableTimezones: string[];
    selectedDateTimestamp: number | null;
    onCalendarSelect: (timestamp: number | null | undefined) => void;
    meetingType?: 'one-on-one' | 'random' | null;
    consultedMember: ConsultedMember | null;
    /** Session-level cover image; takes precedence over member/professional images */
    sessionImageUrl?: string | null;
    professional?: BookingProfessional | null;
    isProfessionalLoading?: boolean;
    freeDays: FreeDay[];
    durations: number[];
    selectedDuration: number;
    onDurationChange: (duration: number) => void;
    isDurationDisabled?: boolean;
    freeHours: string[];
    selectedSlotTimestamp: number | null;
    onTimeSelect: (timestamp: number) => void;
    normalizedSelectedSlotTimestamp: number | null;
    selectedSlotLabel: string;
    statusMessage?: string;
    success?: boolean;
    failure?: boolean;
    onSubmit: (details: ConfirmationDetails) => void;
    bookingDisabled: boolean;
    confirmButtonLabel: string;
    userEmailLabel: string;
    submissionState: 'idle' | 'loading' | 'submitting';
    timePickerVariant?: 'list' | 'listCompact';
    timePickerStartHour: number;
    timePickerEndHour: number;
    timePickerStepMinutes: number;
    onCloseConfirmation?: () => void;
    initialBookingDetails?: {
        clientName?: string;
        clientEmail?: string;
        clientCompany?: string;
    };
    isReschedule?: boolean;
    eventId?: string;
    onCancel?: () => void;
    onReschedule?: () => void;
    showSpamHint?: boolean;
}

export const BookingScheduleControlsMobile = ({
    sessionTitle,
    timezoneName,
    onTimezoneChange,
    availableTimezones,
    selectedDateTimestamp,
    onCalendarSelect,
    meetingType: rawMeetingType,
    consultedMember,
    sessionImageUrl = null,
    professional,
    isProfessionalLoading = false,
    freeDays,
    durations,
    selectedDuration,
    onDurationChange,
    isDurationDisabled = false,
    freeHours,
    onTimeSelect,
    normalizedSelectedSlotTimestamp,
    selectedSlotLabel,
    statusMessage,
    success = false,
    failure = false,
    onSubmit,
    confirmButtonLabel,
    userEmailLabel,
    submissionState,
    timePickerStartHour = 9,
    timePickerEndHour = 16,
    timePickerStepMinutes = 60,
    bookingDisabled,
    initialBookingDetails,
    isReschedule = false,
    eventId,
    onCancel,
    onReschedule,
    showSpamHint = false,
}: BookingScheduleControlsMobileProps) => {
    const meetingType =
        rawMeetingType === 'one-on-one' ? 'one-on-one' : 'random';

    const consultantName =
        meetingType === 'one-on-one'
            ? (consultedMember?.name ?? 'Team member')
            : (consultedMember?.name ?? professional?.title ?? 'Our team');

    const consultantSubtitle =
        meetingType === 'one-on-one'
            ? (consultedMember?.professionalName ?? 'Client Director')
            : (consultedMember?.professionalName ??
              professional?.teaser ??
              'Client Director');

    const consultantImage =
        sessionImageUrl ??
        (meetingType === 'one-on-one'
            ? (consultedMember?.avatarUrl ?? null)
            : (professional?.libraryThumb?.thumbImageFile ?? null));

    const consultantInitial =
        meetingType === 'one-on-one'
            ? consultantName
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((word) => word.charAt(0).toUpperCase())
                  .slice(0, 2)
                  .join('') || '?'
            : (() => {
                  const title = professional?.title ?? consultantName;
                  if (!title) {
                      return '?';
                  }
                  const trimmedTitle = title.trim();
                  if (!trimmedTitle) {
                      return '?';
                  }
                  const lowerTrimmed = trimmedTitle.toLowerCase();
                  const professionalPrefix = 'professional ';
                  if (lowerTrimmed.startsWith(professionalPrefix)) {
                      const withoutPrefix = trimmedTitle
                          .slice(professionalPrefix.length)
                          .trimStart();
                      return withoutPrefix || trimmedTitle;
                  }
                  return trimmedTitle;
              })();

    const professionalCategory =
        meetingType === 'random'
            ? (professional?.categories?.[0] ?? '')
            : (consultedMember?.professionalName ?? '');

    const { user } = useRootData();

    const [userName, setUserName] = useState<string>(() => {
        if (initialBookingDetails?.clientName) {
            return initialBookingDetails.clientName;
        }
        if (user.__typename === 'Me') {
            return [user.FirstName, user.LastName]
                .filter(Boolean)
                .join(' ')
                .trim();
        }
        return '';
    });
    const [userEmail, setUserEmail] = useState<string>(() => {
        if (initialBookingDetails?.clientEmail) {
            return initialBookingDetails.clientEmail;
        }
        return user.__typename === 'Me' ? (user.Email ?? '') : '';
    });
    const [userCompany, setUserCompany] = useState<string>(() => {
        if (initialBookingDetails?.clientCompany) {
            return initialBookingDetails.clientCompany;
        }
        if (user.__typename === 'Me') {
            // If user has more than one professional, default to empty string
            if (user.Professionals.length > 1) {
                return '';
            }
            return user.Professionals[0]?.Title ?? user.Company ?? '';
        }
        return '';
    });
    const [phase, setPhase] = useState<BookingPhase>('schedule');
    const [showValidationErrors, setShowValidationErrors] =
        useState<boolean>(false);

    useEffect(() => {
        // Don't update if rescheduling with initial booking details
        if (isReschedule && initialBookingDetails) {
            if (initialBookingDetails.clientName) {
                setUserName(initialBookingDetails.clientName);
            }
            if (initialBookingDetails.clientEmail) {
                setUserEmail(initialBookingDetails.clientEmail);
            }
            if (initialBookingDetails.clientCompany) {
                setUserCompany(initialBookingDetails.clientCompany);
            }
            return;
        }

        if (user.__typename !== 'Me') {
            return;
        }

        const nextName = [user.FirstName, user.LastName]
            .filter(Boolean)
            .join(' ')
            .trim();
        setUserName(nextName);
        setUserEmail(user.Email ?? '');
        // If user has more than one professional, default to empty string
        if (user.Professionals.length > 1) {
            setUserCompany('');
        } else {
            setUserCompany(user.Professionals[0]?.Title ?? user.Company ?? '');
        }
    }, [user, isReschedule, initialBookingDetails]);

    useEffect(() => {
        if (phase === 'result') {
            return;
        }
        if (!normalizedSelectedSlotTimestamp) {
            setPhase('schedule');
        }
    }, [normalizedSelectedSlotTimestamp, phase]);

    const previousResultStateRef = useRef({
        success,
        failure,
    });

    useEffect(() => {
        const previousSuccess = previousResultStateRef.current.success;
        const previousFailure = previousResultStateRef.current.failure;

        if (!previousSuccess && success) {
            setPhase('result');
        } else if (!previousFailure && failure) {
            setPhase('result');
        }

        previousResultStateRef.current = { success, failure };
    }, [success, failure]);

    useEffect(() => {
        if (phase !== 'confirmation') {
            setShowValidationErrors(false);
        }
    }, [phase]);

    useEffect(() => {
        if (success) {
            setShowValidationErrors(false);
        }
    }, [success]);

    const handleConfirmationClose = () => {
        if (typeof window === 'undefined') {
            return;
        }
        window.location.reload();
    };

    const handleDurationSelect = (value: string) => {
        if (isDurationDisabled) {
            return;
        }

        const parsedValue = Number.parseInt(value, 10);
        if (!Number.isNaN(parsedValue)) {
            onDurationChange(parsedValue);
        }
    };

    const handleTimeSelectInternal = (timestamp: number) => {
        setPhase('schedule');
        onTimeSelect(timestamp);
    };

    const handleContinue = () => {
        if (!normalizedSelectedSlotTimestamp) {
            return;
        }
        setPhase('confirmation');
    };

    const handleBack = () => {
        setPhase('schedule');
        setShowValidationErrors(false);
    };

    const handleConfirm = () => {
        if (isReschedule) {
            // Reschedule fields are read-only and the backend reuses the stored
            // details, so client-side validation could only dead-end the flow
            onSubmit({ userName, userEmail, userCompany });
            return;
        }
        setShowValidationErrors(true);
        const validationResult = confirmationDetailsSchema.safeParse({
            userName,
            userEmail,
            userCompany,
        });

        if (!validationResult.success) {
            return;
        }

        onSubmit(validationResult.data);
    };

    const handleCloseResult = () => {
        if (success) {
            handleConfirmationClose();
            return;
        }

        setPhase('confirmation');
        setShowValidationErrors(false);
    };

    const durationOptions = useMemo(
        () =>
            Array.from(new Set(durations))
                .filter((duration): duration is number =>
                    Number.isFinite(duration),
                )
                .sort((a, b) => a - b)
                .map((duration) => ({
                    value: duration,
                    label: `${duration} minute${duration === 1 ? '' : 's'}`,
                })),
        [durations],
    );

    const disableDurationSelect =
        isDurationDisabled || durationOptions.length === 0;

    const durationSelectValue = durationOptions.some(
        (option) => option.value === selectedDuration,
    )
        ? String(selectedDuration)
        : '';

    const durationPlaceholder = disableDurationSelect
        ? 'No durations available'
        : 'Select duration';

    const hasSelectedSlot =
        typeof selectedSlotLabel === 'string' &&
        selectedSlotLabel.trim().length > 0;

    const isProcessing =
        submissionState === 'submitting' || submissionState === 'loading';
    const isLoggedIn = user.__typename === 'Me';
    // Reschedule shows the stored details locked when the record has an email
    // (the identity anchor — a missing name falls back to it server-side, but a
    // missing email cannot be derived)
    const reschedulePrefilled =
        isReschedule && Boolean(initialBookingDetails?.clientEmail);
    // Non-organisers can't see the stored details on reschedule, so the details
    // form is skipped and the backend reuses the original booking's details
    const rescheduleWithoutDetails = isReschedule && !reschedulePrefilled;
    // Name and email are disabled for logged-in users, but company can always be edited unless prefilled
    const userInputsDisabled =
        isProcessing || isLoggedIn || reschedulePrefilled;
    const companyInputDisabled = isProcessing || reschedulePrefilled;

    const validationResult = useMemo(
        () =>
            confirmationDetailsSchema.safeParse({
                userName,
                userEmail,
                userCompany,
            }),
        [userName, userEmail, userCompany],
    );

    const fieldErrors = validationResult.success
        ? {}
        : validationResult.error.flatten().fieldErrors;

    const nameErrorMessage = fieldErrors.userName?.[0];
    const emailErrorMessage = fieldErrors.userEmail?.[0];
    const companyErrorMessage = fieldErrors.userCompany?.[0];

    const buttonLabel = isProcessing ? 'Booking...' : confirmButtonLabel;

    const formattedDate = normalizedSelectedSlotTimestamp
        ? dayjs(normalizedSelectedSlotTimestamp)
              .tz(timezoneName)
              .format('D MMMM, YYYY')
        : '';

    const formattedTime = normalizedSelectedSlotTimestamp
        ? dayjs(normalizedSelectedSlotTimestamp)
              .tz(timezoneName)
              .format('hh:mm a')
              .toUpperCase()
        : '';

    // Get DST-aware timezone abbreviation
    const timezoneAbbr = getTimezoneShort(
        timezoneName,
        normalizedSelectedSlotTimestamp,
    );

    if (phase === 'result') {
        return (
            <BookingResultMobile
                success={success}
                message={
                    success
                        ? statusMessage || ''
                        : statusMessage ||
                          'Something went wrong while booking your meeting. Please try again or contact support if the problem persists.'
                }
                consultantName={consultantName}
                formattedDate={formattedDate}
                formattedTime={formattedTime}
                timezoneAbbr={timezoneAbbr}
                onClose={handleCloseResult}
                eventId={eventId}
                onCancel={onCancel}
                onReschedule={onReschedule}
                showSpamHint={showSpamHint}
            />
        );
    }

    if (phase === 'confirmation') {
        return (
            <BookingConfirmationMobile
                consultantName={consultantName}
                formattedDate={formattedDate}
                formattedTime={formattedTime}
                timezoneAbbr={timezoneAbbr}
                userName={userName}
                userEmail={userEmail}
                userCompany={userCompany}
                onUserNameChange={setUserName}
                onUserEmailChange={setUserEmail}
                onUserCompanyChange={setUserCompany}
                onBack={handleBack}
                onConfirm={handleConfirm}
                isConfirmDisabled={!hasSelectedSlot || bookingDisabled}
                isLoading={isProcessing}
                confirmButtonLabel={buttonLabel}
                userEmailLabel={userEmailLabel}
                showDetailsForm={!rescheduleWithoutDetails}
                showValidationErrors={showValidationErrors}
                nameErrorMessage={nameErrorMessage}
                emailErrorMessage={emailErrorMessage}
                companyErrorMessage={companyErrorMessage}
                areInputsDisabled={userInputsDisabled}
                isCompanyInputDisabled={companyInputDisabled}
            />
        );
    }

    const title = isReschedule ? 'Reschedule - ' + sessionTitle : sessionTitle;

    return (
        <div className="mx-auto h-screen w-full bg-white text-[#161717]">
            <header className="bg-brick-800 px-6 py-5 text-white">
                <h1 className="text-6 leading-6">
                    {title || 'Book a meeting'}
                </h1>
            </header>

            <div className="space-y-4 pt-6">
                {meetingType === 'random' ? (
                    <section
                        className="flex items-center gap-4 px-4"
                        style={{ opacity: isProfessionalLoading ? 0 : 1 }}
                    >
                        <div
                            className="h-20 w-20 overflow-hidden rounded-full text-center text-base font-semibold text-[#4B4B4B]"
                            style={{
                                background:
                                    professional?.logoBackgroundColor || '',
                            }}
                        >
                            {consultantImage ? (
                                <img
                                    src={professional?.companyLogo || ''}
                                    alt={professional?.title || consultantName}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                    {consultantInitial}
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="text-base leading-6 font-semibold">
                                {professional?.title || consultantName}
                            </p>

                            {professionalCategory ? (
                                <p className="mt-1 text-xs text-[#6C6C6C]">
                                    {professionalCategory}
                                </p>
                            ) : null}
                        </div>
                    </section>
                ) : (
                    <section
                        className="flex items-center gap-4 px-4"
                        style={{ opacity: isProfessionalLoading ? 0 : 1 }}
                    >
                        <div className="h-28 w-24 overflow-hidden rounded-2 bg-[#f3f3f3] text-center text-base font-semibold text-[#4B4B4B]">
                            {consultantImage ? (
                                <img
                                    src={consultantImage}
                                    alt={consultantName}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                    {consultantInitial}
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="text-base leading-6 font-medium">
                                {consultantName}
                            </p>
                            {consultantSubtitle ? (
                                <p className="text-4 font-medium text-[#5e5b5b]">
                                    {consultantSubtitle}
                                </p>
                            ) : null}
                        </div>
                    </section>
                )}

                <section>
                    <CalendarMobile
                        timeZone={timezoneName}
                        onDateSelect={onCalendarSelect}
                        selectedTimestamp={selectedDateTimestamp ?? undefined}
                        freeDays={freeDays}
                    />
                </section>

                <section className="px-4">
                    <p className="text-sm font-semibold text-[#161717]">
                        Meeting duration
                    </p>
                    <div className="mt-3">
                        <Select
                            value={durationSelectValue}
                            onValueChange={handleDurationSelect}
                            disabled={disableDurationSelect}
                        >
                            <SelectTrigger
                                className="h-auto w-full rounded-[14px] border-gray-100 bg-gray-50 px-4 py-3"
                                focusStyle="none"
                            >
                                <div className="flex items-center gap-3">
                                    <Clock3
                                        className="h-4 w-4 shrink-0 text-[#8C7E6B]"
                                        aria-hidden="true"
                                    />
                                    <SelectValue
                                        placeholder={durationPlaceholder}
                                        className="text-base font-medium text-[#3F3F3F]"
                                    />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {durationOptions.map((option) => (
                                    <SelectItem
                                        key={option.value}
                                        value={String(option.value)}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </section>

                {typeof selectedDateTimestamp === 'number' ? (
                    <section className="scrollbar-hide px-4">
                        <TimePickerMobile
                            timeZone={timezoneName}
                            startTimestamp={selectedDateTimestamp}
                            onTimeSelect={handleTimeSelectInternal}
                            onTimezoneChange={onTimezoneChange}
                            availableTimezones={availableTimezones}
                            initialSelectedTime={
                                normalizedSelectedSlotTimestamp ?? undefined
                            }
                            allowTimeZoneChange={true}
                            freeHours={freeHours}
                            startHour={timePickerStartHour}
                            endHour={timePickerEndHour}
                            stepMinutes={timePickerStepMinutes}
                        />
                    </section>
                ) : (
                    <section className="px-4">
                        <div className="rounded-[14px] border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-center text-sm text-[#4B4B4B]">
                            Not available for this day.
                        </div>
                    </section>
                )}
            </div>

            <footer className="sticky bottom-0 mx-4 border-t border-t-[#E0E0E0] bg-white pt-4 pb-20">
                <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!hasSelectedSlot || bookingDisabled}
                    className="h-12 w-full rounded-full bg-brick-600 text-base font-semibold text-white transition-colors hover:bg-[#c56843] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Continue
                </button>
            </footer>
        </div>
    );
};
